import { Injectable } from '@angular/core'

export interface RecentAssignee {
  uid: string
  displayName: string
  role: 'admin' | 'manager' | 'teamlead' | 'employee'
  lastAssignedAt: string
  lastTicketId?: string
}

const KEY_PREFIX = 'awo-care.recentAssignees'
const MAX_ENTRIES = 10
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Per-caller localStorage list of "people you assigned tickets to recently".
 * Powers the "Zuletzt von dir zugewiesen" section in the assignee picker.
 */
@Injectable({ providedIn: 'root' })
export class RecentAssigneesService {
  list(callerUid: string): RecentAssignee[] {
    const raw = this.read(callerUid)
    const cutoff = Date.now() - MAX_AGE_MS
    return raw
      .filter((e) => Date.parse(e.lastAssignedAt) >= cutoff)
      .sort((a, b) => Date.parse(b.lastAssignedAt) - Date.parse(a.lastAssignedAt))
  }

  add(
    callerUid: string,
    assignee: Pick<RecentAssignee, 'uid' | 'displayName' | 'role'>,
    ticketId?: string,
  ): void {
    const existing = this.read(callerUid).filter((e) => e.uid !== assignee.uid)
    const entry: RecentAssignee = {
      uid: assignee.uid,
      displayName: assignee.displayName,
      role: assignee.role,
      lastAssignedAt: new Date().toISOString(),
    }
    if (ticketId) entry.lastTicketId = ticketId
    const next: RecentAssignee[] = [entry, ...existing].slice(0, MAX_ENTRIES)
    localStorage.setItem(this.key(callerUid), JSON.stringify(next))
  }

  private read(callerUid: string): RecentAssignee[] {
    const raw = localStorage.getItem(this.key(callerUid))
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw) as unknown
      if (!Array.isArray(parsed)) return []
      return parsed as RecentAssignee[]
    } catch {
      return []
    }
  }

  private key(callerUid: string): string {
    return `${KEY_PREFIX}.${callerUid}`
  }
}
